import { fulfilment, order, scheduleItem } from "@platform/domain";
import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { CurrencyCode, Money } from "../common/money.js";
import { Quantity } from "../common/quantity.js";
import { Timestamp } from "../common/time.js";
import { Incoterm, TradeAddress, TradeParties, TradeSide } from "./terms.js";
/** An order as either party reads it (ORD-1, cards 053 and 054). */
export const OrderStatus = z.enum(order.states).meta({ id: "OrderStatus" });
export const FulfilmentStatus = z.enum(fulfilment.states).meta({ id: "FulfilmentStatus" });
export const ScheduleItemStatus = z.enum(scheduleItem.states).meta({ id: "ScheduleItemStatus" });
export const OrderLine = z
    .object({
    id: Id,
    productId: Id,
    skuId: Id.nullable(),
    productName: LocalizedText,
    skuCode: z.string().nullable(),
    hsCode: z.string().nullable(),
    quantity: Quantity,
    shippedQuantity: Quantity,
    unit: z.string(),
    unitPrice: Money,
    lineTotal: Money,
    sample: z.boolean().describe("One sample of the product (card 096)"),
})
    .meta({ id: "OrderLine" });
export const OrderScheduleItem = z
    .object({
    id: Id,
    seq: z.number().int().positive(),
    type: z.enum(["deposit", "balance", "full", "top_up"]),
    amount: Money,
    dueTrigger: z.enum(["order_created", "ready_to_ship", "shipped", "change_accepted"]),
    releaseTrigger: z.enum(["receipt_confirmed", "shipment_recorded"]),
    status: ScheduleItemStatus,
    protection: z.enum(["escrow", "none"]).nullable().describe("`none` for a bank transfer (ADR 0005)"),
    method: z.enum(["airwallex", "bank_transfer"]).nullable(),
    dueAt: Timestamp.nullable().describe("When the payment window of a due item ends"),
    paidAt: Timestamp.nullable(),
})
    .meta({ id: "OrderScheduleItem" });
export const OrderAction = z
    .enum([
    "cancel",
    "request_cancellation",
    "accept_cancellation",
    "decline_cancellation",
    "ready_to_ship",
    "record_shipment",
    "confirm_receipt",
    "propose_change",
    "open_case",
    "review",
])
    .meta({
    id: "OrderAction",
    description: "`cancel` before payment, at once; `request_cancellation` after payment and before shipment, which the other side answers; `ready_to_ship` for the shop once paid; `open_case` for the buyer's people who open after-sales cases, on a paid order within the after-sales window with no case open (card 091); `review` for the buyer's people who review, on a completed order within 90 days, once (card 092)",
});
export const CancellationRequestView = z
    .object({
    id: Id,
    requestedBySide: TradeSide,
    reason: z.string(),
    status: z.enum(["pending", "accepted", "declined"]),
    createdAt: Timestamp,
    answeredAt: Timestamp.nullable(),
})
    .meta({ id: "CancellationRequest" });
/** What the order waits for now, for lists, cards, and dashboards. */
export const OrderNextStep = z
    .enum(["payment", "shipment", "confirmation", "none"])
    .meta({ id: "OrderNextStep" });
export const OrderView = z
    .object({
    id: Id,
    number: z.string().describe("Such as `ORD-2026-000123`"),
    status: OrderStatus,
    fulfilmentStatus: FulfilmentStatus,
    side: TradeSide.describe("The caller's side"),
    ...TradeParties,
    inquiryId: Id,
    quoteId: Id,
    quoteVersionNo: z.number().int().positive(),
    conversationId: Id.nullable().describe("The deal conversation it shares with its inquiry (ADR 0020)"),
    settlementCurrency: CurrencyCode,
    listingCurrency: CurrencyCode,
    subtotal: Money,
    charges: z.array(z.object({ kind: z.enum(["freight", "insurance", "other"]), amount: Money })),
    total: Money,
    fxSnapshot: z
        .object({
        base: CurrencyCode,
        quote: CurrencyCode,
        rate: z.string(),
        source: z.string(),
        capturedAt: Timestamp,
    })
        .nullable()
        .describe("The rate both sides were shown at acceptance, when the currencies differ (ADR 0019)"),
    totalInListingCurrency: Money.nullable(),
    lines: z.array(OrderLine),
    schedule: z.array(OrderScheduleItem),
    incoterm: Incoterm,
    namedPlace: z.string(),
    portOfLoading: z.string().nullable(),
    deliveryAddress: TradeAddress,
    shippingOrigin: TradeAddress,
    leadTimeDays: z.number().int().nonnegative(),
    rulesetVersion: z.number().int().positive(),
    confirmationWindowDays: z.number().int().positive(),
    windowTimeZone: z.string(),
    protection: z
        .enum(["escrow", "none"])
        .describe("`none`: paid by bank transfer straight to the seller, with no escrow (ADR 0005)"),
    readyToShipAt: Timestamp.nullable(),
    deliveredAt: Timestamp.nullable().describe("When the last shipment was delivered"),
    confirmationDueAt: Timestamp.nullable().describe("When the buyer's window to confirm receipt ends, in the order's time zone (card 057)"),
    openCaseId: Id.nullable().describe("The after-sales case open on the order, which holds its confirmation"),
    afterSalesUntil: Timestamp.nullable().describe("When the after-sales window ends, counted from delivery in the order's time zone; null before delivery (card 087)"),
    reviewId: Id.nullable().describe("The buyer's review of the order, once written (card 092)"),
    reviewUntil: Timestamp.nullable().describe("When the buyer can no longer review the order: 90 days after completion"),
    confirmedAt: Timestamp.nullable(),
    confirmationMode: z
        .enum(["manual", "timeout"])
        .nullable()
        .describe("`timeout`: confirmed by the platform when the window ended (ORD-3)"),
    completedAt: Timestamp.nullable(),
    cancelledAt: Timestamp.nullable(),
    cancelReason: z.string().nullable(),
    cancellation: CancellationRequestView.nullable().describe("The latest cancellation request, if any"),
    openChangeOrderId: Id.nullable().describe("The change order waiting for an answer, if any (card 059)"),
    nextStep: OrderNextStep,
    createdAt: Timestamp,
    version: z.number().int().positive().describe("Sent as `If-Match` to cancel"),
    actions: z.array(OrderAction).describe("What the caller may do now"),
})
    .meta({ id: "OrderView" });
export const OrderSummary = z
    .object({
    id: Id,
    number: z.string(),
    status: OrderStatus,
    fulfilmentStatus: FulfilmentStatus,
    side: TradeSide,
    ...TradeParties,
    total: Money,
    nextStep: OrderNextStep,
    nextDue: z
        .object({ amount: Money, dueAt: Timestamp })
        .nullable()
        .describe("The earliest item due now, with the end of its window"),
    createdAt: Timestamp,
})
    .meta({ id: "OrderSummary" });
/** The order as its card shows it in the deal conversation, read afresh for each viewer. */
export const OrderCardData = z
    .object({
    number: z.string(),
    status: OrderStatus,
    fulfilmentStatus: FulfilmentStatus,
    nextStep: OrderNextStep,
    ...TradeParties,
    total: Money,
    protection: z.enum(["escrow", "none"]),
    confirmationDueAt: Timestamp.nullable(),
    schedule: z.array(OrderScheduleItem.pick({ seq: true, type: true, amount: true, status: true, dueAt: true })),
    cancellation: CancellationRequestView.pick({ requestedBySide: true, status: true }).nullable(),
})
    .meta({ id: "OrderCardData" });
//# sourceMappingURL=order.view.js.map